import { Alert } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import RichCodeBlock from '../../Common/RichCodeBlock';
import { CodeSkeleton, PageSkeleton } from '../../Common/Skeletons';
import HtmlContent from './FileViewer/HtmlContent';
import MarkdownContent from './FileViewer/MarkdownContent';
import { getLanguageFromFilename } from './FileViewer/languageDetector';
import { decodeTextBytes } from '../utils/decodeTextBytes';
import './FileViewer.css';

/**
 * Check if URL is a CellCog API URL (needs credentials) vs external URL (like
 * GCP signed URLs). Same-origin URLs get credentials; cross-origin signed URLs
 * don't (adding credentials to signed URLs causes CORS failures).
 */
const needsCredentials = (url) => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    return true;
  }
};

/**
 * FileViewer — the unified file viewer for text-based files in the chat panel
 * and the fullscreen viewer.
 *
 * Architecture: this component is now a thin fetch/layout wrapper around
 * `RichCodeBlock` (full-bleed variant). RichCodeBlock itself owns the toolbar
 * (language + Rich/Raw toggle + copy) for every surface, so there is no
 * FileViewer-specific toolbar any more. Raw mode shows the source; Rich mode
 * shows either rendered markdown (MarkdownContent) or a sandboxed HTML iframe
 * (HtmlContent), both passed as the `children` of RichCodeBlock. CSV/TSV files
 * pass no children: RichCodeBlock's built-in CsvPreview renders the data grid.
 *
 * For files that don't support Rich (.py, .json, .yaml, etc.), the toolbar's
 * Rich option is rendered disabled with a tooltip — never hidden — so the
 * chrome is visually identical across files.
 */
const FileViewer = ({ url, filename, isDarkMode, fileType }) => {
  const supportsRich = fileType === 'markdown' || fileType === 'html' || fileType === 'csv';
  const defaultMode = supportsRich ? 'rich' : 'raw';

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const fetchOptions = needsCredentials(url) ? { credentials: 'include' } : {};
        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        // Decode by byte-order mark, never blindly as UTF-8: UTF-16 exports (Google Ads,
        // Excel "Unicode Text") would otherwise decode to NUL-riddled text and fail the
        // binary guard below. BOM-less files decode as UTF-8, as before.
        const { text } = decodeTextBytes(await response.arrayBuffer());

        // Detect misclassified binary content. Catches files that slipped past
        // the media-type detector (e.g. a .txt that is actually binary).
        // CSV is exempt from the U+FFFD check: a latin-1 export with accents decodes
        // to replacement characters, and the user should still see the table.
        if (text.includes('\0') || (fileType !== 'csv' && text.includes('\ufffd'))) {
          throw new Error('File appears to be binary and cannot be displayed as text');
        }

        if (!cancelled) setContent(text);
      } catch (err) {
        console.error('Error loading file:', err);
        if (!cancelled) setError(`Failed to load file: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (url) fetchContent();
    return () => {
      cancelled = true;
    };
  }, [url, fileType]);

  const shikiLanguage = useMemo(() => {
    if (fileType === 'markdown') return 'markdown';
    if (fileType === 'html') return 'html';
    if (fileType === 'csv') {
      // MIME-routed csv (text/csv) may carry an extensionless filename; the language
      // must still be csv/tsv or RichCodeBlock would disable the Rich toggle.
      return getLanguageFromFilename(filename) === 'tsv' ? 'tsv' : 'csv';
    }
    return getLanguageFromFilename(filename);
  }, [fileType, filename]);

  const themeClass = isDarkMode ? 'dark' : 'light';

  if (loading) {
    return (
      <div className={`file-viewer ${themeClass} file-viewer-loading`}>
        {supportsRich ? (
          <div className="file-viewer-skeleton-wrapper">
            <PageSkeleton rows={8} title />
          </div>
        ) : (
          <CodeSkeleton />
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`file-viewer ${themeClass} file-viewer-error`}>
        <Alert
          type="error"
          message="Failed to load file"
          description={
            <div>
              <p>{error}</p>
              <p>Please try downloading the file to view it locally.</p>
            </div>
          }
          showIcon
        />
      </div>
    );
  }

  // Choose what renders inside RichCodeBlock's body slot when mode === 'rich'.
  // - markdown → full-document rendered prose (centered readable column)
  // - html     → sandboxed iframe filling the body
  // - csv      → null: RichCodeBlock's built-in CsvPreview renders the grid
  // - code     → no rich body; RichCodeBlock falls through to Shiki raw view
  const richBody = (() => {
    if (fileType === 'markdown') {
      return <MarkdownContent content={content} isDarkMode={isDarkMode} />;
    }
    if (fileType === 'html') {
      return <HtmlContent content={content} filename={filename} isDarkMode={isDarkMode} />;
    }
    return null;
  })();

  return (
    <div className={`file-viewer ${themeClass}`}>
      <RichCodeBlock
        code={content}
        language={shikiLanguage}
        variant="full-bleed"
        defaultMode={defaultMode}
        className="file-viewer-code-block"
      >
        {richBody}
      </RichCodeBlock>
    </div>
  );
};

export default FileViewer;
